import React, { useEffect, useState } from "react";
import { Select, Spin, Typography, Alert } from "antd";
import { baseUrl } from "../../common/constants";
import _axios from "../../utils/api/_axios";

const { Option } = Select;

// Convert "HH:MM" 24h -> "h:MM AM/PM"
export const to12h = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
};

// Date -> "YYYY-MM-DD" using LOCAL calendar date (not UTC) so a picker
// showing Sept 15 sends "2026-09-15" regardless of user's timezone.
export const toYMD = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d)) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

/**
 * SlotPicker
 *
 * Props:
 *   physicianId, hospitalId, date (Date | "YYYY-MM-DD")
 *   value        currently selected slot { startTime, endTime } | null
 *   onChange     (slot | null) => void
 *   onNoSchedule optional — called with true/false when we learn whether the
 *                physician has any schedule at this facility. Lets the parent
 *                fall back to manual time entry for un-scheduled physicians.
 */
const SlotPicker = ({ physicianId, hospitalId, date, value, onChange, onNoSchedule }) => {
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [hasSchedule, setHasSchedule] = useState(null);
  const [error, setError] = useState(null);
  const [dayName, setDayName] = useState("");

  const ymd = toYMD(date);

  useEffect(() => {
    let cancelled = false;
    if (!physicianId || !hospitalId || !ymd) {
      setSlots([]);
      setHasSchedule(null);
      return;
    }
    setLoading(true);
    setError(null);
    _axios
      .get(`${baseUrl}/availability/slots`, { params: { physicianId, hospitalId, date: ymd } })
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.payload || {};
        setSlots(payload.slots || []);
        setHasSchedule(!!payload.hasSchedule);
        setDayName(payload.dayName || "");
        if (typeof onNoSchedule === "function") onNoSchedule(!payload.hasSchedule);
        // If the previously selected slot is no longer available, clear it
        if (value && !(payload.slots || []).some((s) => s.startTime === value.startTime && s.endTime === value.endTime)) {
          onChange && onChange(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error?.message || err.message);
        setSlots([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physicianId, hospitalId, ymd]);

  if (!physicianId || !hospitalId || !ymd) {
    return <Typography.Text type="secondary">Select a doctor, office and date to see open times.</Typography.Text>;
  }

  if (loading) return <Spin size="small" />;

  if (error) return <Alert type="error" showIcon message={error} />;

  if (hasSchedule === false) {
    return (
      <Alert
        type="warning"
        showIcon
        message="No schedule set for this doctor at this office"
        description="Set up their availability under Availability in the sidebar, or enter a time manually below."
      />
    );
  }

  if (slots.length === 0) {
    return <Alert type="info" showIcon message={`No open times on ${dayName || "this day"}`} description="Try another date." />;
  }

  return (
    <Select
      value={value ? `${value.startTime}|${value.endTime}` : undefined}
      onChange={(v) => {
        if (!v) return onChange && onChange(null);
        const [startTime, endTime] = v.split("|");
        onChange && onChange({ startTime, endTime });
      }}
      placeholder={`${slots.length} open time${slots.length === 1 ? "" : "s"} — select one`}
      className="w-full"
      allowClear
    >
      {slots.map((s) => (
        <Option key={`${s.startTime}|${s.endTime}`} value={`${s.startTime}|${s.endTime}`}>
          {to12h(s.startTime)} – {to12h(s.endTime)}
        </Option>
      ))}
    </Select>
  );
};

export default SlotPicker;
